import React, { useState, useRef, useEffect } from 'react';
import { useContactSearch } from '@/modules/contacts/hooks/useContactSearch';
import type { ContactDropdownItem } from '@/modules/contacts/hooks/useContactsDropdown';
import { Icon } from '@/shared/icons'
/**
 * Props do ContactSearchCombobox
 */
export interface ContactSearchComboboxProps {
  /** Contato selecionado */
  value: ContactDropdownItem | null;
  /** Callback quando contato é selecionado ou removido */
  onChange: (contact: ContactDropdownItem | null) => void;
  /** Callback para abrir AddContactDialog com nome pré-preenchido */
  onRequestCreateContact?: (prefillName: string) => void;
  /** Callback para abrir EditContactDialog */
  onEditContact?: () => void;
  /** Desabilitar input */
  disabled?: boolean;
  /** Erro de validação */
  error?: string;
}

/**
 * Combobox de busca de contatos com opção de criar novo
 *
 * Estados:
 * 1. Idle: Input vazio com placeholder
 * 2. Searching: Digitando, spinner + resultados
 * 3. Results: Lista de contatos encontrados
 * 4. No Results: Opção de criar contato
 * 5. Selected: Chip com contato selecionado
 */
export const ContactSearchCombobox: React.FC<ContactSearchComboboxProps> = ({
  value,
  onChange,
  onRequestCreateContact,
  onEditContact,
  disabled = false,
  error
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Busca com debounce
  const { data: contacts, isLoading } = useContactSearch(searchQuery);

  // Fechar dropdown ao clicar fora
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
        setHighlightedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Selecionar contato
  const handleSelectContact = (contact: ContactDropdownItem) => {
    onChange(contact);
    setSearchQuery('');
    setIsDropdownOpen(false);
    setHighlightedIndex(-1);
  };

  // Limpar seleção
  const handleClear = () => {
    onChange(null);
    setSearchQuery('');
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  // Criar contato via AddContactDialog
  const handleRequestCreate = () => {
    setIsDropdownOpen(false);
    onRequestCreateContact?.(searchQuery);
  };

  // Keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isDropdownOpen || !contacts) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex((prev) => Math.min(prev + 1, contacts.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlightedIndex >= 0 && contacts[highlightedIndex]) {
        handleSelectContact(contacts[highlightedIndex]);
      }
    } else if (e.key === 'Escape') {
      setIsDropdownOpen(false);
      setHighlightedIndex(-1);
    }
  };

  const showDropdown = isDropdownOpen && searchQuery.length >= 2;
  const hasResults = contacts && contacts.length > 0;

  // Se contato já está selecionado, mostrar chip
  if (value) {
    return (
      <div ref={containerRef}>
        {/* `.contact-pick` do mockup: radius 11, padding 12/14, cp-ic 38px slate */}
        <div
          className="flex items-center gap-3 rounded-[11px] border"
          style={{
            padding: '12px 14px',
            backgroundColor: '#ffffff',
            borderColor: error ? '#ef4444' : '#e2e6ec',
          }}
        >
          <div
            className="flex items-center justify-center flex-shrink-0"
            style={{ width: 38, height: 38, borderRadius: '32%', backgroundColor: '#f1f5f9', color: '#94a3b8' }}
          >
            <Icon name="user" className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate" style={{ color: '#1e293b' }}>
              {value.name}
            </p>
            {(value.email || value.phone) && (
              <p className="text-xs truncate" style={{ color: '#64748b' }}>
                {value.email || value.phone}
              </p>
            )}
          </div>
          {!disabled && onEditContact && (
            <button
              type="button"
              onClick={onEditContact}
              className="flex items-center gap-1 px-2 py-1 rounded-md transition-colors flex-shrink-0 text-xs font-medium"
              style={{ color: '#8b5cf6', border: '1px solid #e5e7eb', backgroundColor: 'transparent' }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f5f3ff'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
            >
              <Icon name="edit" className="w-3.5 h-3.5" />
              Editar contato
            </button>
          )}
          {!disabled && (
            <button
              type="button"
              onClick={handleClear}
              className="p-1 rounded transition-colors flex-shrink-0"
              style={{ color: '#94a3b8' }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#e2e8f0';
                e.currentTarget.style.color = '#475569';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = '#94a3b8';
              }}
            >
              <Icon name="x" className="w-4 h-4" />
            </button>
          )}
        </div>
        {error && (
          <p className="text-xs mt-1" style={{ color: '#ef4444' }}>{error}</p>
        )}
      </div>
    );
  }

  return (
    <div ref={containerRef} className="relative">
      {/* `.contact-pick` do mockup: cp-ic 38 + input transparente + `＋Novo` roxo à direita */}
      <div
        className="flex items-center gap-3 rounded-[11px] border transition-colors"
        style={{ padding: '12px 14px', backgroundColor: '#ffffff', borderColor: error ? '#ef4444' : '#e2e6ec' }}
      >
        <div
          className="flex items-center justify-center flex-shrink-0"
          style={{ width: 38, height: 38, borderRadius: '32%', backgroundColor: '#f1f5f9', color: '#94a3b8' }}
        >
          <Icon name="user" className="w-4 h-4" />
        </div>
        <input
          ref={inputRef}
          type="text"
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setIsDropdownOpen(true);
            setHighlightedIndex(-1);
          }}
          onFocus={() => {
            if (searchQuery.length >= 2) setIsDropdownOpen(true);
          }}
          onKeyDown={handleKeyDown}
          placeholder="Buscar contato ou criar novo…"
          className="flex-1 min-w-0 bg-transparent outline-none text-sm"
          style={{ color: '#1e293b' }}
          disabled={disabled}
          data-testid="lead-contact-search"
        />
        {isLoading && searchQuery.length >= 2 && (
          <Icon name="loader" className="w-4 h-4 animate-spin flex-none" style={{ color: '#8b5cf6' }} />
        )}
        {onRequestCreateContact && !disabled && (
          <button
            type="button"
            onClick={() => onRequestCreateContact(searchQuery)}
            className="inline-flex items-center gap-[5px] text-[13px] font-semibold flex-none transition-opacity hover:opacity-80"
            style={{ color: '#7c3aed' }}
            data-testid="lead-contact-new"
          >
            <Icon name="plus" className="w-3.5 h-3.5" />
            Novo
          </button>
        )}
      </div>

      {/* Dropdown de Resultados */}
      {showDropdown && (
        <div
          className="absolute left-0 right-0 z-50 mt-1 rounded-lg overflow-hidden"
          style={{
            backgroundColor: '#ffffff',
            border: '1px solid #e5e7eb',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)',
            maxHeight: '240px',
            overflowY: 'auto',
          }}
        >
          {/* Loading */}
          {isLoading && (
            <div className="flex items-center gap-2 px-3 py-3">
              <Icon name="loader" className="w-4 h-4 animate-spin" style={{ color: '#8b5cf6' }} />
              <span className="text-sm" style={{ color: '#64748b' }}>Buscando contatos...</span>
            </div>
          )}

          {/* Resultados */}
          {!isLoading && hasResults && contacts.map((contact, index) => (
            <button
              key={contact.id}
              type="button"
              className="w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors"
              style={{
                backgroundColor: highlightedIndex === index ? '#f1f5f9' : 'transparent',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f1f5f9';
                setHighlightedIndex(index);
              }}
              onMouseLeave={(e) => {
                if (highlightedIndex !== index) {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }
              }}
              onClick={() => handleSelectContact(contact)}
            >
              <div
                className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: '#f1f5f9', color: '#94a3b8' }}
              >
                <Icon name="user" className="w-3.5 h-3.5" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate" style={{ color: '#1e293b' }}>
                  {contact.name}
                </p>
                <p className="text-xs truncate" style={{ color: '#64748b' }}>
                  {[contact.email, contact.phone].filter(Boolean).join(' · ')}
                </p>
              </div>
            </button>
          ))}

          {/* Sem resultados */}
          {!isLoading && !hasResults && searchQuery.length >= 2 && (
            <div className="px-3 py-2">
              <p className="text-sm mb-2" style={{ color: '#64748b' }}>
                Nenhum contato encontrado
              </p>
            </div>
          )}

          {/* Opção de Criar */}
          {!isLoading && searchQuery.length >= 2 && onRequestCreateContact && (
            <button
              type="button"
              className="w-full flex items-center gap-2 px-3 py-2.5 text-left transition-colors border-t"
              style={{ borderColor: '#f1f5f9' }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f5f3ff'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
              onClick={handleRequestCreate}
            >
              <Icon name="plus" className="w-4 h-4" style={{ color: '#8b5cf6' }} />
              <span className="text-sm font-medium" style={{ color: '#8b5cf6' }}>
                Criar "{searchQuery}"
              </span>
            </button>
          )}
        </div>
      )}

      {error && (
        <p className="text-xs mt-1" style={{ color: '#ef4444' }}>{error}</p>
      )}
    </div>
  );
};

ContactSearchCombobox.displayName = 'ContactSearchCombobox';
